import { getStoredConsent } from "@/components/CookieConsent";

export const MEASUREMENT_ID =
  import.meta.env["VITE_LOVABLE_CONNECTOR_GOOGLE_ANALYTICS_API_KEY"] ||
  "G-KN6K503DPN";

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: Gtag;
  }
}

let configured = false;

/** Global Privacy Control: a legally recognized opt-out signal under CPRA. */
export function gpcEnabled() {
  if (typeof navigator === "undefined") return false;
  return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
}

function analyticsAllowed() {
  return !gpcEnabled() && getStoredConsent() === "all";
}

/**
 * The 164 KiB gtag bundle is only fetched once a visitor has actually opted in,
 * so a no-consent visit (and a Lighthouse run) never downloads it.
 */
function ensureGtagScript() {
  const src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  if (document.querySelector(`script[src="${src}"]`)) return;
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
}

export function initAnalytics() {
  if (typeof window === "undefined") return;
  if (!MEASUREMENT_ID) return;
  if (!analyticsAllowed()) return;
  if (configured) return;

  configured = true;
  ensureGtagScript();


  window.gtag?.("consent", "update", { analytics_storage: "granted" });
  window.gtag?.("config", MEASUREMENT_ID, {
    send_page_view: false,
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
  // Send the current page immediately so accepting consent registers right
  // away instead of waiting for the next navigation.
  window.gtag?.("event", "page_view", { page_path: window.location.pathname });
}

/** Withdraw consent in-session (no reload needed) when a visitor opts out. */
export function revokeAnalytics() {
  if (typeof window === "undefined") return;
  configured = false;
  window.gtag?.("consent", "update", { analytics_storage: "denied" });
}

export function trackPageView(path: string) {
  if (typeof window === "undefined") return;
  if (!window.gtag || !analyticsAllowed()) return;
  window.gtag("event", "page_view", { page_path: path });
}

export function trackEvent(name: string, params?: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  if (!window.gtag || !analyticsAllowed()) return;
  window.gtag("event", name, params ?? {});
}
